/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output} from '@angular/core';
import {NbWindowService} from '@nebular/theme';
import {BaseComponent, Document, DocumentPosition, FileService} from 'compob2b-libs';
import {FileUploader} from 'ng2-file-upload';
import {from, Observable, Subscription} from 'rxjs';
import {concatMap, finalize, map, takeUntil, tap} from 'rxjs/operators';
import {translations} from '../../../../environments/translations.ru';
import {CrudEvent} from '../../interfaces/crud-event';

export interface DocumentFile {

  file: File;
}

@Component({
  selector: 'app-documents-uploader',
  templateUrl: './documents-uploader.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentsUploaderComponent extends BaseComponent implements OnChanges {

  @Input() model: any;
  @Input() uri: string;
  @Output() loadingEvent = new EventEmitter<CrudEvent>();

  uploader: FileUploader;
  uploadURI: string;
  hasDropZoneOver: boolean;
  showUploadForm = false;
  private subscription: Subscription;

  constructor(protected readonly windowService: NbWindowService,
              protected readonly fileService: FileService) {
    super();
    this.uploader = new FileUploader({
      url: null,
      disableMultipart: false,
      formatDataFunctionIsAsync: false,
      isHTML5: true,
      removeAfterUpload: false,
      autoUpload: false,
      maxFileSize: 25 * 1024 * 1024,
    });
  }

  ngOnChanges() {

    if (this.model) {
      this.uploadURI = this.uri + '/upload-document';
    }
  }

  public upload(): void {
    this.loadingEvent.emit(CrudEvent.UPLOADING);
    if (this.uploader.queue.length) {
      this.subscription = from(this.uploader.queue)
        .pipe(
          map(uploadingFile => uploadingFile._file),
          concatMap(uploadingFile => this.uploadFile(uploadingFile)),
          finalize(() => this.finishUpload()),
          takeUntil(this.unsubscribe)
        ).subscribe(resp => this.successLoadingDocument(resp));
      this.hasDropZoneOver = false;
    }
  }

  private finishUpload(): void {
    this.loadingEvent.emit(CrudEvent.UPLOADING_DONE);
    this.uploader.clearQueue();
    this.subscription.unsubscribe();
  }

  private uploadFile(uploadingFile: File): Observable<any> {
    return this.compoApiService.postForm<DocumentFile, string>(this.uploadURI, {file: uploadingFile});
  }

  public fileOver(e: any): void {
    this.hasDropZoneOver = e;
  }

  drop(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.model.documents, event.previousIndex, event.currentIndex);
    this.model.documents.forEach((value, idx) => value.pos = idx);
    this.updateDocumentsPositions();
  }

  updateDocumentsPositions() {
    const uri = this.uri + '/documents';
    const documentPositions: DocumentPosition[] = [];
    this.model.documents.forEach(document => documentPositions.push({id: document.id, pos: document.pos}));
    this.compoApiService.post(uri, documentPositions).pipe(takeUntil(this.unsubscribe)).subscribe(_ => {
    });
  }

  documentUri(fileName: string): string {
    return this.fileService.getPublicDocumentUri(fileName);
  }

  removeDialog(document: Document) {
    this.confirm(translations.confirmDelete + ' ' + translations.file.toLowerCase() + '?', new Observable(() => {
      this.removeDocument(document);
    }));
  }

  removeDocument(document: Document) {
    const uri = this.uri + '/document/' + document.id;
    this.compoApiService.delete<Document>(uri)
      .pipe(
        tap(_ => this.removeDocumentFromModel(document)),
        takeUntil(this.unsubscribe)
      ).subscribe();
  }

  protected removeDocumentFromModel(document: Document) {
    const index = this.model.documents.findIndex(el => el.id === document.id);
    this.model.documents.splice(index, 1);
    this.cdr.detectChanges();
  }

  protected successLoadingDocument(res) {
    this.model.documents.push(res.data);
    this.cdr.detectChanges();
  }
}
